import type { PropType } from 'vue';
import type { Nullable } from '../../types';
import type { TDropdownProps } from './TDropdown.types';
import { merge } from 'es-toolkit/compat';
import {
  computed,
  defineComponent,
  onMounted,
  onUnmounted,
  ref,
  Teleport,
  watch,
} from 'vue';
import { useMenuStyles, useStyles } from './TDropdown.styles';
import { getVisibleElements } from './TDropdown.utils';

const defaultProps = {
  size: 'small',
  position: 'center',
};

export const TDropdown = defineComponent({
  name: 'TDropdown',
  props: {
    open: Boolean as PropType<TDropdownProps['open']>,
    size: String as PropType<TDropdownProps['size']>,
    position: String as PropType<TDropdownProps['position']>,
    fullWidth: Boolean as PropType<TDropdownProps['fullWidth']>,
    attachToBody: Boolean as PropType<TDropdownProps['attachToBody']>,
  },
  emits: ['cancel'],
  setup(_props, { slots, attrs, emit }) {
    const props = computed(() => merge({}, defaultProps, _props, attrs));

    const container = ref<Nullable<HTMLDivElement>>(null);
    const trigger = ref<Nullable<HTMLDivElement>>(null);
    const menu = ref<Nullable<HTMLDivElement>>(null);
    const menuPosition = ref<{
      top: number;
      left: number;
    }>({ top: 0, left: 0 });

    const calculateMenuPosition = () => {
      if (!trigger.value || !menu.value) {
        return;
      }
      const rect = trigger.value.getBoundingClientRect();

      const baseTop = rect.bottom + 8;
      const baseLeft = rect.left;

      let top = baseTop;
      let left = baseLeft;

      switch (props.value.position) {
        case 'center':
          left
            = baseLeft
              - menu.value.clientWidth / 2
              + trigger.value.clientWidth / 2;
          break;
        case 'right':
          left = baseLeft + trigger.value.clientWidth;
          break;
        case 'left':
          left = baseLeft - menu.value.clientWidth;
          break;
        case 'middle-left':
          left = baseLeft - menu.value.clientWidth + trigger.value.clientWidth;
          break;
        case 'middle-right':
          left = baseLeft;
          break;
        case 'top-center':
          top = rect.top - menu.value.clientHeight - 8;
          left
            = baseLeft
              - menu.value.clientWidth / 2
              + trigger.value.clientWidth / 2;
          break;
        case 'top-left':
          top = rect.top - menu.value.clientHeight - 8;
          left = baseLeft - menu.value.clientWidth;
          break;
        case 'top-right':
          top = rect.top - menu.value.clientHeight - 8;
          left = baseLeft + trigger.value.clientWidth;
          break;
      }

      menuPosition.value = { top, left };
    };

    const menuBaseClass = useMenuStyles();
    const menuClasses = computed(() => [
      menuBaseClass.value,
      `${menuBaseClass.value}--${props.value.size}`,
      `${menuBaseClass.value}--${props.value.position}`,
      {
        [`${menuBaseClass.value}--open`]: props.value.open,
      },
    ]);
    const menuStyles = computed(() => ({
      top: `${menuPosition.value.top}px`,
      left: `${menuPosition.value.left}px`,
      width: props.value.fullWidth
        ? `${trigger.value?.clientWidth}px`
        : undefined,
    }));

    const menuHeight = computed(() => menu.value?.clientHeight ?? 0);
    const baseClass = useStyles(menuHeight);
    const classes = computed(() => [
      baseClass.value,
      `${baseClass.value}--${props.value.size}`,
      `${baseClass.value}--${props.value.position}`,
      {
        [`${baseClass.value}--active`]: props.value.open,
        [`${baseClass.value}--full-width`]: props.value.fullWidth,
      },
    ]);

    const handleClickOutside = (event: Event) => {
      if (
        props.value.open
        && container.value
        && !container.value.contains(event.target as Node)
        && !(event.target as Element)?.closest(`.${menuBaseClass.value}`)
      ) {
        emit('cancel');
      }
    };

    const handleKeydown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        emit('cancel');
      }
    };

    const handleMenuInitialFocus = (event: KeyboardEvent) => {
      if (props.value.attachToBody && event.key === 'Tab') {
        event.preventDefault();
        const firstMenuItem = menu.value?.querySelector('[role="menuitem"]');
        if (firstMenuItem) {
          (firstMenuItem as HTMLElement).focus();
        }
      }
    };

    if (props.value.attachToBody) {
      watch(
        [
          () => props.value.open,
          () => props.value.position,
          () => props.value.size,
          trigger,
          menu,
        ],
        () => {
          calculateMenuPosition();
          if (props.value.open && menu.value) {
            trigger.value?.addEventListener('keydown', handleMenuInitialFocus);
          } else {
            trigger.value?.removeEventListener(
              'keydown',
              handleMenuInitialFocus,
            );
          }
        },
        { immediate: true },
      );
    }

    onMounted(() => {
      if (props.value.attachToBody) {
        window.addEventListener('scroll', calculateMenuPosition);
        window.addEventListener('resize', calculateMenuPosition);
      }
      document.addEventListener('click', handleClickOutside);
      document.addEventListener('keydown', handleKeydown);
    });

    onUnmounted(() => {
      if (props.value.attachToBody) {
        window.removeEventListener('scroll', calculateMenuPosition);
        window.removeEventListener('resize', calculateMenuPosition);
      }
      document.removeEventListener('click', handleClickOutside);
      document.removeEventListener('keydown', handleKeydown);
    });

    const visibleElements = computed(() => getVisibleElements(slots.default?.() || []));

    const renderMenu = () => (
      <div
        ref={menu}
        class={
          props.value.attachToBody
            ? menuClasses.value
            : `${baseClass.value}__menu`
        }
        style={props.value.attachToBody ? menuStyles.value : undefined}
        role="menu"
        tabindex={props.value.attachToBody ? 0 : undefined}
      >
        {visibleElements.value.map((element, index, array) => (
          <div key={index}>
            {element}
            {index < array.length - 1 && (
              <div class={`${baseClass.value}__divider`} />
            )}
          </div>
        ))}
      </div>
    );

    return () => (
      <div ref={container} class={classes.value}>
        <div ref={trigger}>{slots.trigger?.()}</div>
        {props.value.open
        && (props.value.attachToBody
          ? (
              <Teleport to="body">{renderMenu()}</Teleport>
            )
          : (
              renderMenu()
            ))}
      </div>
    );
  },
});
